import { useState, useMemo } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { X, LogOut } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { useWorkspace } from '@/core/providers/WorkspaceProvider'
import { usePermissions } from '@/hooks/usePermissions'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { BOTTOM_TABS, MOBILE_MENU_ITEMS, MOBILE_ADMIN_ITEMS } from '@/core/constants/navigation'
import type { NavigationItem } from '@/core/types/workspace'

export function MobileNavigation() {
  const [menuOpen, setMenuOpen] = useState(false)
  const navigate = useNavigate()
  const { esAdmin } = useOrganizacion()
  const { workspaceActual, getNavigation } = useWorkspace()
  const { puede } = usePermissions()
  const { logout } = useAuth0()

  // Workspace navigation items resolved from getNavigation().
  // Filtro seguro de permisos: solo oculta un item si su permiso está
  // explícitamente en false (permisos no mapeados se muestran).
  const workspaceItems: NavigationItem[] = useMemo(() => {
    try {
      return getNavigation().filter(
        (item) => !item.permission || puede[item.permission as keyof typeof puede] !== false
      )
    } catch {
      return []
    }
  }, [getNavigation, puede])

  const handleMenuItemClick = (href: string) => {
    if (!href || href === '#') return
    setMenuOpen(false)
    navigate(href)
  }

  const handleLogout = () => {
    setMenuOpen(false)
    logout({ logoutParams: { returnTo: window.location.origin } })
  }

  return (
    <>
      {/* Bottom Tab Bar */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 flex h-16 items-center justify-around border-t bg-card">
        {BOTTOM_TABS.map((tab) => {
          if (tab.id === 'mas') {
            return (
              <button
                key={tab.id}
                onClick={() => setMenuOpen(true)}
                className="flex flex-col items-center justify-center gap-0.5 py-1 px-2 text-muted-foreground"
              >
                <tab.icon className="h-5 w-5" />
                <span className="text-[10px] leading-tight">{tab.name}</span>
              </button>
            )
          }

          return (
            <NavLink
              key={tab.id}
              to={tab.href}
              end={tab.href === '/'}
              className={({ isActive }) =>
                cn(
                  'flex flex-col items-center justify-center gap-0.5 py-1 px-2',
                  isActive
                    ? 'text-primary'
                    : 'text-muted-foreground'
                )
              }
            >
              <tab.icon className="h-5 w-5" />
              <span className="text-[10px] leading-tight">{tab.name}</span>
            </NavLink>
          )
        })}
      </nav>

      {/* Full Menu Sheet */}
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="bottom" className="rounded-t-xl max-h-[80vh] p-0">
          <SheetHeader className="px-4 pt-4 pb-2 border-b">
            <div className="flex items-center justify-between">
              <SheetTitle>Menu</SheetTitle>
              <button
                onClick={() => setMenuOpen(false)}
                aria-label="Cerrar menú"
                className="rounded-sm p-1 opacity-70 hover:opacity-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </SheetHeader>
          <div className="overflow-y-auto py-2">
            {/* Main Menu Items — mismo filtro seguro de permisos que workspaceItems */}
            <div className="px-2">
              {MOBILE_MENU_ITEMS.filter(
                (item) => !item.permission || puede[item.permission as keyof typeof puede] !== false
              ).map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleMenuItemClick(item.href)}
                  className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-foreground hover:bg-accent/50 transition-colors"
                >
                  <item.icon className="h-4 w-4 text-muted-foreground" />
                  {item.name}
                </button>
              ))}
            </div>

            {/* Workspace Items */}
            {workspaceItems.length > 0 && (
              <>
                <div className="mx-4 my-2 border-t" />
                <p className="px-5 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {workspaceActual.nombre}
                </p>
                <div className="px-2">
                  {workspaceItems.map((item) => {
                    const Icon = item.icon
                    return (
                      <button
                        key={item.id ?? item.href}
                        onClick={() => handleMenuItemClick(item.href)}
                        className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-foreground hover:bg-accent/50 transition-colors"
                      >
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        {item.name}
                      </button>
                    )
                  })}
                </div>
              </>
            )}

            {/* Admin Items */}
            {esAdmin && (
              <>
                <div className="mx-4 my-2 border-t" />
                <p className="px-5 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Administracion
                </p>
                <div className="px-2">
                  {MOBILE_ADMIN_ITEMS.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleMenuItemClick(item.href)}
                      className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-foreground hover:bg-accent/50 transition-colors"
                    >
                      <item.icon className="h-4 w-4 text-muted-foreground" />
                      {item.name}
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* Cerrar sesión — en móvil no hay footer de sidebar con logout */}
            <div className="mx-4 my-2 border-t" />
            <div className="px-2 pb-2">
              <button
                onClick={handleLogout}
                className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm text-destructive hover:bg-destructive/10 transition-colors"
              >
                <LogOut className="h-4 w-4" />
                Cerrar sesion
              </button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}
